'use client'

import { exportGraphic } from '@/lib/api'
import { useGraphicHistory } from '@/lib/features/graphic/GraphicHistoryContext'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { Graphic } from 'sprites-types/api/v2/graphics'
import { useGraphic } from '../../lib/features/graphic/GraphicContext'
import { GraphicPreviewActionsProvider } from '../../lib/features/graphic/GraphicPreviewActionsContext'
import { useGraphicPreview } from '../../lib/features/graphic/GraphicPreviewContext'

interface CopilotGraphicActionHandlerProps {
  children: React.ReactNode
}

export function CopilotGraphicActionHandler({ children }: CopilotGraphicActionHandlerProps) {
  const { reset } = useGraphic()
  const { clearAllStates } = useGraphicPreview()
  const { addGraphic, getCurrentEditingInitialId, setCurrentEditingInitialId } = useGraphicHistory()
  const [isExporting, setIsExporting] = useState(false)

  const handleGraphicSave = useCallback(
    async (graphic: Graphic) => {
      setIsExporting(true)
      try {
        const result = await exportGraphic(graphic.id)

        if (!result.success) {
          console.error('Error exporting graphic:', result.error)
          return
        }

        const initialGraphicId = getCurrentEditingInitialId()

        if (initialGraphicId) {
          addGraphic(initialGraphicId, graphic.id, result.url!)
        }

        reset()
        clearAllStates()
      } finally {
        setIsExporting(false)
      }
    },
    [getCurrentEditingInitialId, addGraphic, reset, clearAllStates]
  )

  const handleGraphicDiscard = useCallback(() => {
    setCurrentEditingInitialId(null)
    clearAllStates()
    reset()
    console.log('Graphic discarded')
  }, [setCurrentEditingInitialId, clearAllStates, reset])

  return (
    <>
      <GraphicPreviewActionsProvider
        onGraphicSave={handleGraphicSave}
        onGraphicDiscard={handleGraphicDiscard}
      >
        {children}
      </GraphicPreviewActionsProvider>

      {/* Export loading overlay - rendered as portal */}
      {isExporting &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="flex items-center space-x-3 rounded-lg bg-white p-4 shadow-lg">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-green-500"></div>
              <span className="text-gray-700">Saving graphic...</span>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
